import React, { useEffect, useState, useCallback } from "react";
import { Button, Card, Col, Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import DataTable from "react-data-table-component";
import moment from "moment";
import { saveAs } from "file-saver";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useDispatch, useSelector } from "react-redux";
import { LotSmartActions } from "../../Actions";
import DatatableHeader from "../DatatableHeader/DatatableHeader";
import Loader from "../Loader/Loader";
import DateRangeAndAmount from "../DateRangeAndAmount/DateRangeAndAmount";

// ─── Expandable row ────────────────────────────────────────────────────────────

const ExpandedRow = ({ data: row }) => (
  <div className="extend-row">
    <Row className="expandedView mb-1">
      <Col>
        <Row>
          <Col xs="4"><b>Transaction ID</b></Col>
          <Col xs="8">{row.transactionId || "-"}</Col>
        </Row>
      </Col>
      <Col>
        <Row>
          <Col xs="4"><b>Terminal Time</b></Col>
          <Col xs="8">
            {row.transmissionDateTime
              ? moment(row.transmissionDateTime, "YYYYMMDDHHmmss").format("MMM D, YYYY h:mm:ss A")
              : "-"}
          </Col>
        </Row>
      </Col>
      <Col></Col>
    </Row>
  </div>
);

// ─── Status badge ──────────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  if (!status) return <span className="text-muted">—</span>;
  const upper = status.toUpperCase();
  if (upper === "APPROVED" || upper === "APPROVAL") return <div style={{ color: "green" }}>Approved</div>;
  if (upper === "DECLINED") return <div style={{ color: "red" }}>Declined</div>;
  if (upper === "REFUNDED") return <div style={{ color: "orange" }}>Refunded</div>;
  return <div>{status}</div>;
};

const TransactionStatus = ({ type }) => {
  if (!type) return <span className="text-muted">—</span>;
  const upper = type.toUpperCase();
  if (upper === "SALE" || upper === "PAID" || upper === "PURCHASE") return <div style={{ color: "green" }}>Paid</div>;
  if (upper === "DECLINED" || upper === "FAILED" || upper === "DECLINE") return <div style={{ color: "red" }}>Declined</div>;
  if (upper === "REFUND" || upper === "REFUNDED" || upper === "RETURN") return <div style={{ color: "orange" }}>Refunded</div>;
  if (upper === "VOID" || upper === "VOIDED") return <div style={{ color: "gray" }}>Void</div>;
  return <div>{type}</div>;
};

// ─── Table overflow styles ─────────────────────────────────────────────────────

const customStyles = {
  rows: {
    style: {
      minHeight: "72px",
    },
  },
  headCells: {
    style: {
      paddingLeft: "8px",
      paddingRight: "8px",
    },
  },
  cells: {
    style: {
      paddingLeft: "8px",
      paddingRight: "8px",
    },
  },
  tableWrapper: { style: { overflow: "auto" } },
  responsiveWrapper: { style: { overflow: "auto" } },
};

// ─── Main component ────────────────────────────────────────────────────────────

function LotSmartLogs() {
  const dispatch = useDispatch();
  const { property_id } = useSelector((s) => s.UserProfileDetails);
  const { merchants, auditLogs, auditCount } = useSelector((s) => s.LotSmartDetails);
  const { backgroundColor } = useSelector((s) => s.Logindetails);
  const main_body = backgroundColor?.MainBackground;

  const today = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const [startDate, setStartDate] = useState(thirtyDaysAgo);
  const [endDate, setEndDate] = useState(today);
  const [selectedMerchantId, setSelectedMerchantId] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loader, setLoader] = useState(false);

  const hideSlider = () => {
    document.body.classList.remove("foc-open");
  };

  const resetAll = () => {
    setSelectedMerchantId("");
    setStartDate(thirtyDaysAgo);
    setEndDate(today);
  };

  useEffect(() => {
    if (property_id) {
      dispatch(LotSmartActions.getLotSmartMerchants(property_id));
    }
  }, [dispatch, property_id]);

  const loadLogs = useCallback(async (pageNum = 1, pageLimit = limit) => {
    setLoader(true);
    const params = {
      property_id,
      page: pageNum,
      limit: pageLimit,
      startDate: startDate ? moment(startDate).startOf("day").toISOString() : undefined,
      endDate: endDate ? moment(endDate).endOf("day").toISOString() : undefined,
    };
    if (selectedMerchantId) params.merchant_id = selectedMerchantId;
    await dispatch(LotSmartActions.getLotSmartAuditLogs(params));
    setLoader(false);
  }, [dispatch, property_id, startDate, endDate, selectedMerchantId, limit]);

  useEffect(() => {
    if (property_id) loadLogs(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property_id]);

  const handleSearch = () => {
    setPage(1);
    hideSlider();
    loadLogs(1);
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    loadLogs(newPage, limit);
  };

  const handleRowsPerPageChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
    loadLogs(1, newLimit);
  };

  const handleExport = async () => {
    const params = {
      property_id,
      startDate: startDate ? moment(startDate).startOf("day").toISOString() : undefined,
      endDate: endDate ? moment(endDate).endOf("day").toISOString() : undefined,
    };
    if (selectedMerchantId) params.merchant_id = selectedMerchantId;
    const bufferData = await dispatch(LotSmartActions.exportLotSmartAuditLogs(params));
    if (bufferData) {
      saveAs(
        new Blob([bufferData], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
        `LOTSMART-TRANSACTIONS-${moment().format("YYYYMMDD-HHmmss")}.xlsx`,
      );
    }
  };

  const columns = [
    {
      name: "Transaction Time",
      selector: (r) => r.created_at,
      sortable: true,
      width: "165px",
      cell: (r) => (r.created_at ? moment(r.created_at).format("YYYY-MM-DD HH:mm:ss") : "—"),
    },
    {
      name: "Meter",
      selector: (r) => r.mid,
      width: "180px",
      cell: (r) => (
        <OverlayTrigger delay={{ show: 250, hide: 400 }} placement="top" overlay={<Tooltip id="tooltip-mid">{r.mid}</Tooltip>}>
          <div className="text-nowrap text-truncate pr-2">{r.mid || "—"}</div>
        </OverlayTrigger>
      ),
    },
    {
      name: "Transaction ID",
      selector: (r) => r.transactionId,
      width: "160px",
      cell: (r) => (
        <OverlayTrigger delay={{ show: 250, hide: 400 }} placement="top" overlay={<Tooltip id="tooltip-tid">{r.transactionId}</Tooltip>}>
          <div className="text-nowrap text-truncate pr-2">{r.transactionId || "—"}</div>
        </OverlayTrigger>
      ),
    },
    {
      name: "Payment Status",
      selector: (r) => r.status,
      width: "150px",
      cell: (r) => <StatusBadge status={r.status} />,
    },
    {
      name: "Payment Type",
      selector: (r) => r.paymentType,
      width: "125px",
      cell: (r) => r.paymentType || "—",
    },
    {
      name: "Card Type",
      selector: (r) => r.cardType,
      width: "110px",
      cell: (r) => r.cardType || "—",
    },
    {
      name: "Status",
      selector: (r) => r.transactionType,
      width: "120px",
      cell: (r) => <TransactionStatus type={r.transactionType} />,
    },
    {
      name: "Amount",
      selector: (r) => r.approvedAmount,
      width: "90px",
      cell: (r) => r.approvedAmount || "—",
    },
    {
      name: "Card Number",
      selector: (r) => r.pan,
      width: "145px",
      cell: (r) => r.pan || "—",
    },
  ];

  const filteredLogs = searchValue
    ? auditLogs.filter((r) =>
        [r.mid, r.tid, r.status, r.paymentType, r.authCode, r.cardType, r.transactionType, r.approvedAmount, r.pan]
          .some((v) => v && String(v).toLowerCase().includes(searchValue.toLowerCase()))
      )
    : auditLogs;

  return (
    <div>
      <Card className="mb-0">
        <Card.Body className="pos-rel-overflow-hide">
          <>
            <div className="sidebarFilter">
              <Card className="contact-container mb-0">
                <Card.Header style={{ backgroundColor: main_body }}>
                  <div className="d-flex align-items-center justify-content-between">
                    <Card.Title as="h4">LotSmart Transactions</Card.Title>
                    <a
                      onClick={hideSlider}
                      href="javascript:void(0);"
                      className="btn btn-sm border-0 bg-light rounded-circle w-30px h-30px d-flex align-items-center justify-content-center"
                    >
                      <i className="fa fa-times" aria-hidden="true"></i>
                    </a>
                  </div>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md="12">
                      <Form.Group controlId="merchantSelect">
                        <Form.Label>Merchant:</Form.Label>
                        <Form.Control as="select" value={selectedMerchantId} onChange={(e) => setSelectedMerchantId(e.target.value)}>
                          <option value="">All Merchants</option>
                          {merchants.map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.device_label || m.mid || `Merchant #${m.id}`}
                            </option>
                          ))}
                        </Form.Control>
                      </Form.Group>
                    </Col>
                    <Col md="12">
                      <Form.Group>
                        <label className="mb-0 mr-2">Start Date:</label>
                        <DatePicker
                          className="form-control"
                          placeholderText="Select Start Date"
                          selected={startDate}
                          onChange={(date) => setStartDate(date)}
                          dateFormat="MM/dd/yyyy"
                          startDate={startDate}
                          endDate={endDate}
                          maxDate={new Date()}
                        />
                      </Form.Group>
                    </Col>
                    <Col md="12">
                      <Form.Group>
                        <label className="mb-0 mr-2">End Date:</label>
                        <DatePicker
                          className="form-control"
                          placeholderText="Select End Date"
                          selected={endDate}
                          onChange={(date) => setEndDate(date)}
                          dateFormat="MM/dd/yyyy"
                          startDate={startDate}
                          endDate={endDate}
                          minDate={startDate}
                          maxDate={new Date()}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
                <Card.Footer className="text-right justify-content-between d-flex">
                  <Button variant="info" className="h-40 custom-btn-secondary btn-sm" onClick={resetAll} disabled={loader}>
                    Reset
                  </Button>
                  <Button variant="info" className="h-40 btn-fill btn-sm ml-2" onClick={handleSearch} disabled={loader}>
                    Search
                  </Button>
                </Card.Footer>
              </Card>
            </div>
            <header>
              <div className="active-permit-header sm-flex-column sm-align-items-start">
                <h5 className="mb-2 mb-md-0">LotSmart Transactions</h5>
                <div className="filter-button d-flex flex-wrap">
                  <div className="d-flex align-items-center search-mb-0">
                    <DatatableHeader
                      className="mt-0 mb-0"
                      searchValue={searchValue}
                      HandleChange={handleSearchChange}
                    />
                  </div>
                  <div className="ml-2">
                    <Button
                      variant="info"
                      className="btn-fill btn-sm"
                      onClick={handleExport}
                      disabled={auditLogs.length === 0}
                      size="medium"
                    >
                      Export
                    </Button>
                  </div>
                </div>
              </div>
            </header>
          </>
          {loader ? (
            <Loader />
          ) : (
            <>
              <div className="citation-list-table has-parent-tab">
                <DataTable
                  columns={columns}
                  data={filteredLogs}
                  customStyles={customStyles}
                  expandableRows
                  expandableRowsComponent={ExpandedRow}
                  expandOnRowClicked
                  expandableRowsHideExpander={false}
                  pagination
                  paginationServer
                  paginationTotalRows={auditCount}
                  paginationPerPage={limit}
                  paginationDefaultPage={page}
                  onChangePage={handlePageChange}
                  onChangeRowsPerPage={handleRowsPerPageChange}
                  paginationRowsPerPageOptions={[10, 25, 50, 100]}
                  fixedHeader
                  fixedHeaderScrollHeight="calc(100vh - 320px)"
                  noDataComponent="No records found for the selected filters."
                  dense
                />
              </div>
              <DateRangeAndAmount
                ReportList={auditLogs}
                amountName="approvedAmount"
                startDateTime={startDate ? moment(startDate).format("MMM D, YYYY") : ""}
                endDateTime={endDate ? moment(endDate).format("MMM D, YYYY") : ""}
              />
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

export default LotSmartLogs;
